import {contentSetsApi} from 'common/src/modules/contentSets/contentSets.api';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {z} from 'zod';
import {CampaignSetupAgentToolName} from '../../campaignSetupAgent.enums';

const getContentSetsToolSchema = z.object({
  prompt: z
    .string()
    .describe(
      'Detailed description of the content set to search for following the user prompt. If a specific content set ID is mentioned, include it in the prompt.',
    ),
});

function getContentSetsTool() {
  async function execute({
    input: {prompt},
  }: ToolExecuteParams<typeof getContentSetsToolSchema>): Promise<ToolResult> {
    try {
      const searchResponse = await contentSetsApi.searchContentSets(prompt);

      if (!searchResponse.contentSets.length) {
        return toolResult(`No content sets found. Reason: ${searchResponse.filtersExplanation}`);
      }

      const contentSets = searchResponse.contentSets;

      const instructions = [
        contentSets.length > 1
          ? 'More than one content set found, use the first content set and ask the user for clarification.'
          : '',
        'When setting the content set for the card, you must always set the card feed ID to match the content set feed ID.',
      ]
        .filter(Boolean)
        .join(' ');

      return toolResult(
        JSON.stringify({
          contentSets: contentSets.map((set) => ({
            id: set.id,
            name: set.name,
            feedId: set.feedId,
          })),
          instructions,
        }),
      );
    } catch (error) {
      return toolResult(
        `Error retrieving content sets: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      );
    }
  }

  return tool({
    name: CampaignSetupAgentToolName.GetContentSets,
    displayName: 'Searching content sets',
    description: 'Search for content sets from the account library.',
    execute,
    inputSchema: getContentSetsToolSchema,
  });
}

export default getContentSetsTool;
